import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  CalendarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import ImageUpload from '@/components/ui/ImageUpload';
import ImagePreviewModal from '@/components/ui/ImagePreviewModal';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import { cn } from '@/utils/cn';
import { getFullImageUrl, getInitials } from '@/utils/imageUrl';
import { executiveProtectionService, type ProtectedPerson as APIProtectedPerson, type Activity as APIActivity } from '@/services/executive-protection.service';
import { tripBriefsService, type BriefStatus } from '@/services/trip-briefs.service';
import MedicalInformationSection from '@/components/executive-protection/MedicalInformationSection';

// Status badge colors for the read-only Trip Briefs card (mirrors TripPlanner).
const TRIP_BRIEF_STATUS_BADGES: Record<BriefStatus, string> = {
  DRAFT: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200',
  GENERATING: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200',
  READY: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  ARCHIVED: 'bg-secondary-100 text-secondary-500 dark:bg-secondary-800 dark:text-secondary-400',
};

// Map API types to local component types
interface ProtectedPerson extends APIProtectedPerson {
  lastLocation?: string;
  notes?: string;
}

interface EditFormData {
  firstName: string;
  lastName: string;
  title: string;
  department: string;
  phone: string;
  email: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  status: 'active' | 'inactive' | 'travel';

  // Home Address
  homeAddressLine1: string;
  homeAddressLine2: string;
  homeCity: string;
  homeState: string;
  homeZipCode: string;
  homeCountry: string;

  // Emergency Contact
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  emergencyContactEmail: string;

  // Medical Information
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  insuranceGroupNumber?: string;

  primaryDoctorName?: string;
  primaryDoctorPhone?: string;
  primaryDoctorAddress?: string;

  medicalConditions?: string[];

  medicalDisclosureConsent?: boolean;
  consentDate?: string;
  consentExpiresAt?: string;

  notes: string;
}

// MissionCard component for displaying scheduled missions
function MissionCard({ activity }: { activity: APIActivity }) {
  // Normalize status to lowercase for display mapping (backend uses uppercase)
  const normalizedStatus = activity.status.toLowerCase();

  const statusColors: Record<string, string> = {
    scheduled: 'bg-primary-500 text-white dark:bg-primary-600 dark:text-white',
    planned: 'bg-primary-500 text-white dark:bg-primary-600 dark:text-white', // Backend uses PLANNED
    in_progress: 'bg-warning-500 text-white dark:bg-warning-600 dark:text-white',
    completed: 'bg-success-500 text-white dark:bg-success-600 dark:text-white',
    cancelled: 'bg-secondary-400 text-white dark:bg-secondary-600 dark:text-white',
  };

  const typeColors = {
    meeting: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
    travel: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
    event: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
    other: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();

    if (isToday) {
      return 'Today';
    }

    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Link to={`/security/executive-protection/activities/${activity.id}`} className="block">
      <Card hover="default" className="transition-all duration-200 hover:shadow-lg">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <h4 className="font-medium text-secondary-900 dark:text-white mb-1">
                {activity.title}
              </h4>
              <div className="flex gap-2">
                <span className={cn('px-2 py-1 text-xs font-medium rounded-full', typeColors[activity.type ?? 'other'])}>
                  {(activity.type ?? 'other').toUpperCase()}
                </span>
                <span className={cn('px-2 py-1 text-xs font-medium rounded-full', statusColors[normalizedStatus] ?? statusColors.scheduled)}>
                  {normalizedStatus.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-sm">
              <CalendarIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                {formatDate(activity.startTime ?? activity.startDatetime ?? new Date().toISOString())}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <ClockIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                {formatTime(activity.startTime ?? activity.startDatetime ?? new Date().toISOString())} - {formatTime(activity.endTime ?? activity.endDatetime ?? new Date().toISOString())}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <MapPinIcon className="w-4 h-4 text-secondary-500 dark:text-secondary-400" />
              <span className="text-secondary-700 dark:text-secondary-300">
                {activity.location ?? 'Location not specified'}
              </span>
            </div>
          </div>

          {activity.description && (
            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-3 line-clamp-2">
              {activity.description}
            </p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export default function ExecutiveDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof EditFormData, string>>>({});
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [newProfilePicture, setNewProfilePicture] = useState<File | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch protected person data from API
  const { data: person, isLoading, error } = useQuery({
    queryKey: ['executive-protection', 'person', id],
    queryFn: async () => {
      if (!id) { return null; }
      const data = await executiveProtectionService.getProtectedPersonById(id);
      return data ? { ...data, lastLocation: data.location } : null;
    },
    enabled: !!id,
    staleTime: 30000, // Cache data for 30 seconds to reduce redundant fetches
    refetchOnMount: true, // Only refetch if data is stale (not always)
  });

  // Fetch activities for this person
  const { data: activities = [] } = useQuery({
    queryKey: ['executive-protection', 'activities', id],
    queryFn: async () => {
      const allMissions = await executiveProtectionService.getActivities();
      // Filter to only this person's activities that are planned or in progress
      return allMissions
        .filter(activity =>
          activity.protectedPersonId === id &&
          (activity.status === 'PLANNED' || activity.status === 'IN_PROGRESS')
        )
        .sort((a, b) => {
          const aTime = a.startTime ?? a.startDatetime ?? new Date().toISOString();
          const bTime = b.startTime ?? b.startDatetime ?? new Date().toISOString();
          return new Date(aTime).getTime() - new Date(bTime).getTime();
        });
    },
    enabled: !!id,
  });

  // Fetch trip briefs for this person (read-only profile surfacing — AC-7).
  const { data: tripBriefs = [] } = useQuery({
    queryKey: ['trip-briefs', 'by-person', id],
    queryFn: () => tripBriefsService.list({ protectedPersonId: id }),
    enabled: !!id,
    staleTime: 30000,
  });

  // Update mutation
  const updatePersonMutation = useMutation({
    mutationFn: (data: Partial<ProtectedPerson>) => {
      if (!id) { throw new Error('No ID provided'); }
      return executiveProtectionService.updateProtectedPerson(id, data);
    },
    // Don't invalidate queries here - will be done manually in handleSave
    // to avoid multiple rapid invalidations that cause rate limit issues
  });

  const [editData, setEditData] = useState<EditFormData>({
    firstName: person?.firstName ?? '',
    lastName: person?.lastName ?? '',
    title: person?.title ?? '',
    department: person?.department ?? '',
    phone: person?.phone ?? '',
    email: person?.email ?? '',
    riskLevel: (person?.riskLevel ?? 'medium') as 'low' | 'medium' | 'high' | 'critical',
    status: person?.status ?? 'active',
    homeAddressLine1: person?.homeAddressLine1 ?? '',
    homeAddressLine2: person?.homeAddressLine2 ?? '',
    homeCity: person?.homeCity ?? '',
    homeState: person?.homeState ?? '',
    homeZipCode: person?.homeZipCode ?? '',
    homeCountry: person?.homeCountry ?? 'USA',
    emergencyContactName: person?.emergencyContactName ?? '',
    emergencyContactRelationship: person?.emergencyContactRelationship ?? '',
    emergencyContactPhone: person?.emergencyContactPhone ?? '',
    emergencyContactEmail: person?.emergencyContactEmail ?? '',
    insuranceProvider: person?.insuranceProvider ?? '',
    insurancePolicyNumber: person?.insurancePolicyNumber ?? '',
    insuranceGroupNumber: person?.insuranceGroupNumber ?? '',
    primaryDoctorName: person?.primaryDoctorName ?? '',
    primaryDoctorPhone: person?.primaryDoctorPhone ?? '',
    primaryDoctorAddress: person?.primaryDoctorAddress ?? '',
    medicalConditions: person?.medicalConditions ?? [],
    medicalDisclosureConsent: person?.medicalDisclosureConsent ?? false,
    consentDate: person?.consentDate ?? '',
    consentExpiresAt: person?.consentExpiresAt ?? '',
    notes: person?.notes ?? '',
  });

  // Update editData when person data loads
  React.useEffect(() => {
    if (person && !isEditing) {
      setEditData({
        firstName: person.firstName,
        lastName: person.lastName,
        title: person.title ?? '',
        department: person.department ?? '',
        phone: person.phone ?? '',
        email: person.email ?? '',
        riskLevel: (person.riskLevel ?? 'medium') as 'low' | 'medium' | 'high' | 'critical',
        status: person.status ?? 'active',
        homeAddressLine1: person.homeAddressLine1 ?? '',
        homeAddressLine2: person.homeAddressLine2 ?? '',
        homeCity: person.homeCity ?? '',
        homeState: person.homeState ?? '',
        homeZipCode: person.homeZipCode ?? '',
        homeCountry: person.homeCountry ?? 'USA',
        emergencyContactName: person.emergencyContactName ?? '',
        emergencyContactRelationship: person.emergencyContactRelationship ?? '',
        emergencyContactPhone: person.emergencyContactPhone ?? '',
        emergencyContactEmail: person.emergencyContactEmail ?? '',
        insuranceProvider: person.insuranceProvider ?? '',
        insurancePolicyNumber: person.insurancePolicyNumber ?? '',
        insuranceGroupNumber: person.insuranceGroupNumber ?? '',
        primaryDoctorName: person.primaryDoctorName ?? '',
        primaryDoctorPhone: person.primaryDoctorPhone ?? '',
        primaryDoctorAddress: person.primaryDoctorAddress ?? '',
        medicalConditions: person.medicalConditions ?? [],
        medicalDisclosureConsent: person.medicalDisclosureConsent ?? false,
        consentDate: person.consentDate ?? '',
        consentExpiresAt: person.consentExpiresAt ?? '',
        notes: person.notes ?? '',
      });
    }
  }, [person, isEditing]);

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto mt-20 text-center">
          <p className="text-secondary-600 dark:text-secondary-400">Loading protected person...</p>
        </div>
      </div>
    );
  }

  if (error ?? !person) {
    return (
      <div className="p-6">
        <div className="max-w-md mx-auto mt-20 text-center">
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white mb-4">
            Protected Person Not Found
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400 mb-6">
            The protected person you&apos;re looking for doesn&apos;t exist or has been removed.
          </p>
          <Button onClick={() => navigate('/security/executive-protection')}>
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Executive Protection
          </Button>
        </div>
      </div>
    );
  }

  const riskColors: Record<string, string> = {
    low: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    medium: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
    critical: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
  };

  const statusColors: Record<string, string> = {
    active: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    inactive: 'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400',
    travel: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300',
    office: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
    off_duty: 'bg-secondary-100 text-secondary-600 dark:bg-secondary-800 dark:text-secondary-400',
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleEdit = () => {
    setEditData({
      firstName: person.firstName,
      lastName: person.lastName,
      title: person.title ?? '',
      department: person.department ?? '',
      phone: person.phone ?? '',
      email: person.email ?? '',
      riskLevel: (person.riskLevel ?? 'medium') as 'low' | 'medium' | 'high' | 'critical',
      status: person.status ?? 'active',
      homeAddressLine1: person.homeAddressLine1 ?? '',
      homeAddressLine2: person.homeAddressLine2 ?? '',
      homeCity: person.homeCity ?? '',
      homeState: person.homeState ?? '',
      homeZipCode: person.homeZipCode ?? '',
      homeCountry: person.homeCountry ?? 'USA',
      emergencyContactName: person.emergencyContactName ?? '',
      emergencyContactRelationship: person.emergencyContactRelationship ?? '',
      emergencyContactPhone: person.emergencyContactPhone ?? '',
      emergencyContactEmail: person.emergencyContactEmail ?? '',
      insuranceProvider: person.insuranceProvider ?? '',
      insurancePolicyNumber: person.insurancePolicyNumber ?? '',
      insuranceGroupNumber: person.insuranceGroupNumber ?? '',
      primaryDoctorName: person.primaryDoctorName ?? '',
      primaryDoctorPhone: person.primaryDoctorPhone ?? '',
      primaryDoctorAddress: person.primaryDoctorAddress ?? '',
      medicalConditions: person.medicalConditions ?? [],
      medicalDisclosureConsent: person.medicalDisclosureConsent ?? false,
      consentDate: person.consentDate ?? '',
      consentExpiresAt: person.consentExpiresAt ?? '',
      notes: person.notes ?? '',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditData({
      firstName: person.firstName,
      lastName: person.lastName,
      title: person.title ?? '',
      department: person.department ?? '',
      phone: person.phone ?? '',
      email: person.email ?? '',
      riskLevel: (person.riskLevel ?? 'medium') as 'low' | 'medium' | 'high' | 'critical',
      status: person.status ?? 'active',
      homeAddressLine1: person.homeAddressLine1 ?? '',
      homeAddressLine2: person.homeAddressLine2 ?? '',
      homeCity: person.homeCity ?? '',
      homeState: person.homeState ?? '',
      homeZipCode: person.homeZipCode ?? '',
      homeCountry: person.homeCountry ?? 'USA',
      emergencyContactName: person.emergencyContactName ?? '',
      emergencyContactRelationship: person.emergencyContactRelationship ?? '',
      emergencyContactPhone: person.emergencyContactPhone ?? '',
      emergencyContactEmail: person.emergencyContactEmail ?? '',
      insuranceProvider: person.insuranceProvider ?? '',
      insurancePolicyNumber: person.insurancePolicyNumber ?? '',
      insuranceGroupNumber: person.insuranceGroupNumber ?? '',
      primaryDoctorName: person.primaryDoctorName ?? '',
      primaryDoctorPhone: person.primaryDoctorPhone ?? '',
      primaryDoctorAddress: person.primaryDoctorAddress ?? '',
      medicalConditions: person.medicalConditions ?? [],
      medicalDisclosureConsent: person.medicalDisclosureConsent ?? false,
      consentDate: person.consentDate ?? '',
      consentExpiresAt: person.consentExpiresAt ?? '',
      notes: person.notes ?? '',
    });
    setErrors({});
    setNewProfilePicture(null);
    setIsEditing(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof EditFormData, string>> = {};

    if (!editData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!editData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!editData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!editData.department.trim()) {
      newErrors.department = 'Department is required';
    }
    if (!editData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]+$/.test(editData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    if (!editData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // First update the person data
      await updatePersonMutation.mutateAsync({
        firstName: editData.firstName,
        lastName: editData.lastName,
        title: editData.title,
        department: editData.department,
        phone: editData.phone,
        email: editData.email,
        riskLevel: editData.riskLevel,
        status: editData.status,
        location: person.location,
        homeAddressLine1: editData.homeAddressLine1,
        homeAddressLine2: editData.homeAddressLine2,
        homeCity: editData.homeCity,
        homeState: editData.homeState,
        homeZipCode: editData.homeZipCode,
        homeCountry: editData.homeCountry,
        emergencyContactName: editData.emergencyContactName,
        emergencyContactRelationship: editData.emergencyContactRelationship,
        emergencyContactPhone: editData.emergencyContactPhone,
        emergencyContactEmail: editData.emergencyContactEmail,
        insuranceProvider: editData.insuranceProvider,
        insurancePolicyNumber: editData.insurancePolicyNumber,
        insuranceGroupNumber: editData.insuranceGroupNumber,
        primaryDoctorName: editData.primaryDoctorName,
        primaryDoctorPhone: editData.primaryDoctorPhone,
        primaryDoctorAddress: editData.primaryDoctorAddress,
        medicalConditions: editData.medicalConditions,
        medicalDisclosureConsent: editData.medicalDisclosureConsent,
        consentDate: editData.consentDate,
        consentExpiresAt: editData.consentExpiresAt,
        notes: editData.notes,
      });

      // If there's a new profile picture, upload it
      if (newProfilePicture && id) {
        setIsUploadingImage(true);
        try {
          await executiveProtectionService.uploadProfilePicture(id, newProfilePicture);
          setNewProfilePicture(null);
        } catch (uploadError) {
          console.error('Failed to upload profile picture:', uploadError);
          // Don't fail the entire save if just the image upload fails
        } finally {
          setIsUploadingImage(false);
        }
      }

      // Invalidate queries ONCE after all operations complete
      // This prevents multiple rapid invalidations that trigger rate limits
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'person', id] });
      queryClient.invalidateQueries({ queryKey: ['executive-protection', 'persons'] });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update person:', error);
    }
  };

  const handleChange = (field: keyof EditFormData, value: string) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleDelete = async () => {
    if (!id) { return; }

    setIsDeleting(true);
    try {
      await executiveProtectionService.deleteProtectedPerson(id);
      // Invalidate queries and navigate back to list
      queryClient.invalidateQueries({ queryKey: ['executive-protection'] });
      navigate('/security/executive-protection');
    } catch (error) {
      console.error('Failed to delete executive:', error);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      key={`person-${person?.id}-${person?.updatedAt}`}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
      layout
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <Link
              to="/security/executive-protection"
              className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </Link>
            {/* Profile Picture */}
            {getFullImageUrl(person.profilePictureUrl) ? (
              <button
                onClick={() => setIsImageModalOpen(true)}
                className="w-16 h-16 rounded-lg border-2 border-primary-200 dark:border-primary-800 hover:border-primary-300 dark:hover:border-primary-700 transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                aria-label="View full size profile picture"
              >
                <img
                  src={getFullImageUrl(person.profilePictureUrl) ?? undefined}
                  alt={person.name}
                  className="w-full h-full rounded-lg object-cover"
                />
              </button>
            ) : (
              <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-semibold text-lg">
                  {getInitials(person.name)}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
                {person.name}
              </h1>
              <p className="text-secondary-600 dark:text-secondary-400 mt-1">
                {person.title}
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            {!isEditing ? (
              <>
                <Button
                  variant="destructive"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={person.status === 'inactive'}
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Delete
                </Button>
                <Button onClick={handleEdit} disabled={person.status === 'inactive'}>
                  <PencilIcon className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleCancel} disabled={isUploadingImage}>
                  <XMarkIcon className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isUploadingImage}>
                  <CheckIcon className="w-4 h-4 mr-2" />
                  {isUploadingImage ? 'Uploading Image...' : 'Save Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Status Badges */}
      <motion.div variants={itemVariants}>
        <div className="flex flex-wrap gap-2">
          <span className={cn('px-3 py-1 text-xs font-medium rounded-full', riskColors[person.riskLevel ?? 'medium'])}>
            {(person.riskLevel ?? 'medium').toUpperCase()} RISK
          </span>
          <span className={cn('px-3 py-1 text-xs font-medium rounded-full', statusColors[person.status ?? 'active'])}>
            {(person.status ?? 'active').replace('_', ' ').toUpperCase()}
          </span>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isEditing ? (
                <>
                  <div className="flex items-center space-x-3">
                    <UserIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Full Name</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{person.name}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <BuildingOfficeIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Title</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{person.title}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <BuildingOfficeIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Department</div>
                      <div className="font-medium text-secondary-900 dark:text-white">{person.department}</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Profile Picture Upload in Edit Mode */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Profile Picture
                    </label>
                    <ImageUpload
                      onImageSelect={(file) => setNewProfilePicture(file)}
                      currentImageUrl={newProfilePicture ? undefined : getFullImageUrl(person.profilePictureUrl) ?? undefined}
                      maxSizeMB={5}
                    />
                    {newProfilePicture && (
                      <p className="mt-2 text-sm text-success-600 dark:text-success-400">
                        New image selected. Save changes to upload.
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={editData.firstName}
                        onChange={(e) => handleChange('firstName', e.target.value)}
                        className={cn(
                          'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                          errors.firstName ? 'border-danger-300' : 'border-secondary-300 dark:border-secondary-600',
                          'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white'
                        )}
                      />
                      {errors.firstName && <p className="mt-1 text-sm text-danger-600">{errors.firstName}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={editData.lastName}
                        onChange={(e) => handleChange('lastName', e.target.value)}
                        className={cn(
                          'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                          errors.lastName ? 'border-danger-300' : 'border-secondary-300 dark:border-secondary-600',
                          'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white'
                        )}
                      />
                      {errors.lastName && <p className="mt-1 text-sm text-danger-600">{errors.lastName}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={editData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                      className={cn(
                        'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.title ? 'border-danger-300' : 'border-secondary-300 dark:border-secondary-600',
                        'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white'
                      )}
                    />
                    {errors.title && <p className="mt-1 text-sm text-danger-600">{errors.title}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Department *
                    </label>
                    <input
                      type="text"
                      value={editData.department}
                      onChange={(e) => handleChange('department', e.target.value)}
                      className={cn(
                        'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.department ? 'border-danger-300' : 'border-secondary-300 dark:border-secondary-600',
                        'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white'
                      )}
                    />
                    {errors.department && <p className="mt-1 text-sm text-danger-600">{errors.department}</p>}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact Information */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isEditing ? (
                <>
                  {person.phone && (
                    <div className="flex items-center space-x-3">
                      <PhoneIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                      <div>
                        <div className="text-sm text-secondary-600 dark:text-secondary-400">Phone</div>
                        <div className="font-medium text-secondary-900 dark:text-white">{person.phone}</div>
                      </div>
                    </div>
                  )}
                  {person.email && (
                    <div className="flex items-center space-x-3">
                      <EnvelopeIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                      <div>
                        <div className="text-sm text-secondary-600 dark:text-secondary-400">Email</div>
                        <div className="font-medium text-secondary-900 dark:text-white">{person.email}</div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      className={cn(
                        'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.phone ? 'border-danger-300' : 'border-secondary-300 dark:border-secondary-600',
                        'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white'
                      )}
                    />
                    {errors.phone && <p className="mt-1 text-sm text-danger-600">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={editData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={cn(
                        'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                        errors.email ? 'border-danger-300' : 'border-secondary-300 dark:border-secondary-600',
                        'bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white'
                      )}
                    />
                    {errors.email && <p className="mt-1 text-sm text-danger-600">{errors.email}</p>}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Security Information */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>Security Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isEditing ? (
                <>
                  <div className="flex items-center space-x-3">
                    <ExclamationTriangleIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Risk Level</div>
                      <div className="font-medium text-secondary-900 dark:text-white capitalize">{person.riskLevel}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <UserIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                    <div>
                      <div className="text-sm text-secondary-600 dark:text-secondary-400">Status</div>
                      <div className="font-medium text-secondary-900 dark:text-white capitalize">
                        {(person.status ?? 'active').replace('_', ' ')}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Risk Level
                    </label>
                    <select
                      value={editData.riskLevel}
                      onChange={(e) => handleChange('riskLevel', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Status
                    </label>
                    <select
                      value={editData.status}
                      onChange={(e) => handleChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                    >
                      <option value="active">Active</option>
                      <option value="travel">Travel</option>
                      <option value="office">Office</option>
                      <option value="off_duty">Off Duty</option>
                    </select>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Location & Activity */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>Location & Activity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-3">
                <MapPinIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                <div>
                  <div className="text-sm text-secondary-600 dark:text-secondary-400">Last Location</div>
                  <div className="font-medium text-secondary-900 dark:text-white">
                    {person.lastLocation ?
                      `${person.lastLocation.latitude.toFixed(4)}, ${person.lastLocation.longitude.toFixed(4)}` :
                      'No location data'
                    }
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <ClockIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
                <div>
                  <div className="text-sm text-secondary-600 dark:text-secondary-400">Last Updated</div>
                  <div className="font-medium text-secondary-900 dark:text-white">{person.lastUpdate ? formatDate(person.lastUpdate) : 'Not available'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Home Address */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>Home Address</CardTitle>
            </CardHeader>
            <CardContent>
              {!isEditing ? (
                <>
                  {(person.homeAddressLine1 ?? person.homeCity) ? (
                    <div className="space-y-2 text-secondary-900 dark:text-white">
                      {person.homeAddressLine1 && <div>{person.homeAddressLine1}</div>}
                      {person.homeAddressLine2 && <div>{person.homeAddressLine2}</div>}
                      {(person.homeCity ?? person.homeState ?? person.homeZipCode) && (
                        <div>
                          {person.homeCity && <span>{person.homeCity}</span>}
                          {person.homeCity && person.homeState && <span>, </span>}
                          {person.homeState && <span>{person.homeState}</span>}
                          {person.homeZipCode && <span> {person.homeZipCode}</span>}
                        </div>
                      )}
                      {person.homeCountry && <div>{person.homeCountry}</div>}
                    </div>
                  ) : (
                    <p className="text-secondary-500 dark:text-secondary-400">No address on file</p>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={editData.homeAddressLine1}
                      onChange={(e) => handleChange('homeAddressLine1', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={editData.homeAddressLine2}
                      onChange={(e) => handleChange('homeAddressLine2', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      placeholder="Apt, suite, unit, etc."
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        City
                      </label>
                      <input
                        type="text"
                        value={editData.homeCity}
                        onChange={(e) => handleChange('homeCity', e.target.value)}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                        placeholder="City"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        State
                      </label>
                      <input
                        type="text"
                        value={editData.homeState}
                        onChange={(e) => handleChange('homeState', e.target.value)}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                        placeholder="State"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        ZIP Code
                      </label>
                      <input
                        type="text"
                        value={editData.homeZipCode}
                        onChange={(e) => handleChange('homeZipCode', e.target.value)}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                        placeholder="ZIP Code"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        value={editData.homeCountry}
                        onChange={(e) => handleChange('homeCountry', e.target.value)}
                        className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Emergency Contact */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle>Emergency Contact</CardTitle>
            </CardHeader>
            <CardContent>
              {!isEditing ? (
                <>
                  {person.emergencyContactName ? (
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <UserIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400 mt-0.5" />
                        <div>
                          <div className="text-sm text-secondary-600 dark:text-secondary-400">Name</div>
                          <div className="font-medium text-secondary-900 dark:text-white">{person.emergencyContactName}</div>
                        </div>
                      </div>
                      {person.emergencyContactRelationship && (
                        <div className="flex items-start space-x-3">
                          <UserIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400 mt-0.5" />
                          <div>
                            <div className="text-sm text-secondary-600 dark:text-secondary-400">Relationship</div>
                            <div className="font-medium text-secondary-900 dark:text-white">{person.emergencyContactRelationship}</div>
                          </div>
                        </div>
                      )}
                      {person.emergencyContactPhone && (
                        <div className="flex items-start space-x-3">
                          <PhoneIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400 mt-0.5" />
                          <div>
                            <div className="text-sm text-secondary-600 dark:text-secondary-400">Phone</div>
                            <div className="font-medium text-secondary-900 dark:text-white">{person.emergencyContactPhone}</div>
                          </div>
                        </div>
                      )}
                      {person.emergencyContactEmail && (
                        <div className="flex items-start space-x-3">
                          <EnvelopeIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400 mt-0.5" />
                          <div>
                            <div className="text-sm text-secondary-600 dark:text-secondary-400">Email</div>
                            <div className="font-medium text-secondary-900 dark:text-white">{person.emergencyContactEmail}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-secondary-500 dark:text-secondary-400">No emergency contact on file</p>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Contact Name
                    </label>
                    <input
                      type="text"
                      value={editData.emergencyContactName}
                      onChange={(e) => handleChange('emergencyContactName', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      placeholder="e.g., Jane Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Relationship
                    </label>
                    <input
                      type="text"
                      value={editData.emergencyContactRelationship}
                      onChange={(e) => handleChange('emergencyContactRelationship', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      placeholder="e.g., Spouse, Parent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={editData.emergencyContactPhone}
                      onChange={(e) => handleChange('emergencyContactPhone', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={editData.emergencyContactEmail}
                      onChange={(e) => handleChange('emergencyContactEmail', e.target.value)}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                      placeholder="emergency@example.com"
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Medical Information Section */}
      <motion.div variants={itemVariants}>
        <MedicalInformationSection
          isEditing={isEditing}
          personId={id}
          medicalData={{
            insuranceProvider: isEditing ? editData.insuranceProvider : person.insuranceProvider,
            insurancePolicyNumber: isEditing ? editData.insurancePolicyNumber : person.insurancePolicyNumber,
            insuranceGroupNumber: isEditing ? editData.insuranceGroupNumber : person.insuranceGroupNumber,
            primaryDoctorName: isEditing ? editData.primaryDoctorName : person.primaryDoctorName,
            primaryDoctorPhone: isEditing ? editData.primaryDoctorPhone : person.primaryDoctorPhone,
            primaryDoctorAddress: isEditing ? editData.primaryDoctorAddress : person.primaryDoctorAddress,
            medicalConditions: isEditing ? editData.medicalConditions : person.medicalConditions,
            medicalDisclosureConsent: isEditing ? editData.medicalDisclosureConsent : person.medicalDisclosureConsent,
            consentDate: isEditing ? editData.consentDate : person.consentDate,
            consentExpiresAt: isEditing ? editData.consentExpiresAt : person.consentExpiresAt,
          }}
          onMedicalDataChange={(data) => {
            setEditData(prev => ({ ...prev, ...data }));
            // Clear errors for changed fields
            if (data.consentExpiresAt !== undefined && errors.consentExpiresAt) {
              setErrors(prev => ({ ...prev, consentExpiresAt: undefined }));
            }
          }}
          onInsuranceCardUpload={async (file, type) => {
            if (!id) { return; }
            try {
              const attachmentType = type === 'front' ? 'INSURANCE_CARD_FRONT' : 'INSURANCE_CARD_BACK';
              await executiveProtectionService.uploadMedicalAttachment(id, file, attachmentType);
              // Note: Attachments will be refreshed when the form is saved (batched invalidation)
              // This prevents multiple rapid API calls that trigger rate limits
            } catch (error) {
              console.error(`Failed to upload insurance card ${type}:`, error);
            }
          }}
          onConsentFormUpload={async (file) => {
            if (!id) { return; }
            try {
              await executiveProtectionService.uploadMedicalAttachment(id, file, 'CONSENT_FORM');
              // Note: Attachments will be refreshed when the form is saved (batched invalidation)
              // This prevents multiple rapid API calls that trigger rate limits
            } catch (error) {
              console.error('Failed to upload consent form:', error);
            }
          }}
          errors={errors}
        />
      </motion.div>

      {/* Notes Section */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
            <CardDescription>Additional information and observations</CardDescription>
          </CardHeader>
          <CardContent>
            {!isEditing ? (
              <p className="text-secondary-700 dark:text-secondary-300">
                {person.notes ?? 'No notes available.'}
              </p>
            ) : (
              <textarea
                value={editData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
                placeholder="Add any additional notes or observations..."
              />
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Scheduled Missions Section */}
      {person.status !== 'inactive' && (
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CalendarIcon className="w-5 h-5 mr-2" />
                Scheduled Missions
              </CardTitle>
              <CardDescription>Upcoming and ongoing activities for this executive</CardDescription>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <p className="text-secondary-500 dark:text-secondary-400 text-center py-8">
                  No scheduled activities found
                </p>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {activities.map((activity) => (
                    <motion.div
                      key={activity.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <MissionCard activity={activity} />
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Trip Briefs Section (read-only — full workflow lives in the Trip Planner) */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              Trip Briefs
            </CardTitle>
            <CardDescription>Travel Risk Briefs generated for this executive</CardDescription>
          </CardHeader>
          <CardContent>
            {tripBriefs.length === 0 ? (
              <p className="text-secondary-500 dark:text-secondary-400 text-center py-8">
                No trip briefs for this executive yet
              </p>
            ) : (
              <div className="space-y-2">
                {tripBriefs.map((brief) => (
                  <Link
                    key={brief.id}
                    to="/security/executive-protection/trip-planner"
                    className="flex items-center justify-between gap-3 rounded-lg border border-secondary-200 p-3 transition-colors hover:bg-secondary-50 dark:border-secondary-700 dark:hover:bg-secondary-800/50"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-secondary-500 dark:text-secondary-400">{brief.briefNumber}</span>
                        <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', TRIP_BRIEF_STATUS_BADGES[brief.status])}>
                          {brief.status}
                        </span>
                      </div>
                      <p className="truncate text-sm font-medium text-secondary-900 dark:text-white">{brief.title}</p>
                    </div>
                    <div className="whitespace-nowrap text-xs text-secondary-500 dark:text-secondary-400">
                      {brief.departDate.slice(0, 10)} → {brief.returnDate.slice(0, 10)}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Inactive Warning Banner */}
      {person.status === 'inactive' && (
        <motion.div variants={itemVariants} className="mb-6">
          <div className="bg-warning-50 dark:bg-warning-900/20 border-l-4 border-warning-500 p-4 rounded-lg">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-5 h-5 text-warning-600 dark:text-warning-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-warning-800 dark:text-warning-300">
                  This Executive is Inactive
                </h3>
                <p className="text-sm text-warning-700 dark:text-warning-400 mt-1">
                  This executive has been marked as inactive and can no longer be edited or have activities tracked against them.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={person.profilePictureUrl ? getFullImageUrl(person.profilePictureUrl) : null}
        altText={person.name}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Executive"
        message={`Are you sure you want to delete ${person.name}? This will mark them as inactive. They will still appear in the list but will no longer be editable or have activities tracked.`}
        confirmText="Delete Executive"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </motion.div>
  );
}
